import { useState, useEffect, useMemo, useRef } from "react";
import { X, ImagePlus, Trash2, Loader2, ChevronDown } from "lucide-react";
import { Product } from "../../../types/product";
import { Tax } from "../../../types/tax";
import { getProductTaxes } from "../../../services/productsApi";
import { useAuth } from "../../../auth/AuthContext";
import { useToast } from "../../../hooks/useToast";

interface ProductFormProps {
  product?: Product | null;
  onSave: (product: Omit<Product, "id"> & { id?: string }) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const ProductForm = ({
  product,
  onSave,
  onCancel,
  isSubmitting = false,
}: ProductFormProps) => {
  const { user } = useAuth();
  const toast = useToast();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    barcode: "",
    selectedTaxIds: [] as number[], // Array of selected tax IDs
    price: "",
    discountValue: "0",
    discountType: "amount" as "percent" | "amount", // "amount" = "Fixed"
    description: "",
    images: [] as string[],
  });

  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [loadingTaxes, setLoadingTaxes] = useState(false);
  const [isTaxDropdownOpen, setIsTaxDropdownOpen] = useState(false);
  const [taxSearchQuery, setTaxSearchQuery] = useState("");
  const taxDropdownRef = useRef<HTMLDivElement>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState<number>(-1);
  const [isDragging, setIsDragging] = useState(false);
  const [brokenImageIndices, setBrokenImageIndices] = useState<Set<number>>(new Set());

  // Fetch taxes when component mounts
  useEffect(() => {
    const fetchTaxes = async () => {
      setLoadingTaxes(true);
      try {
        const taxesData = await getProductTaxes();
        setTaxes(taxesData);
      } catch (error) {
        console.error("Error fetching taxes:", error);
        setTaxes([]);
      } finally {
        setLoadingTaxes(false);
      }
    };
    fetchTaxes();
  }, []);

  useEffect(() => {
    if (product) {
      // Extract tax IDs as array
      const taxIds: number[] = [];
      if ((product as any).taxIds && Array.isArray((product as any).taxIds) && (product as any).taxIds.length > 0) {
        taxIds.push(...(product as any).taxIds);
      }

      // Extract discount info
      const discountValue = (product as any).discount !== undefined ? (product as any).discount.toString() : "0";
      const discountType = (product as any).discountType === "amount" ? "amount" : "percent";

      // Extract images
      const productImages = (product as any).images && Array.isArray((product as any).images) && (product as any).images.length > 0
        ? (product as any).images
        : product.image
          ? [product.image]
          : [];

      setFormData({
        name: product.name || "",
        sku: (product as any).sku || "",
        barcode: (product as any).barcode || "",
        selectedTaxIds: taxIds,
        price: product.price?.toString() || "",
        discountValue,
        discountType,
        description: (product as any).description || "",
        images: productImages,
      });

      // Set existing image URLs (from API) and new files separately
      setExistingImageUrls(productImages);
      setImageFiles([]);
      setDefaultImageIndex(productImages.length > 0 ? 0 : -1);
    } else {
      // Reset form when adding new product
      setFormData({
        name: "",
        sku: "",
        barcode: "",
        selectedTaxIds: [],
        price: "",
        discountValue: "0",
        discountType: "amount",
        description: "",
        images: [],
      });
      setImageFiles([]);
      setExistingImageUrls([]);
      setDefaultImageIndex(-1);
      setBrokenImageIndices(new Set()); // Reset broken images
    }
  }, [product]);

  const discountedPrice = useMemo(() => {
    const priceNum = parseFloat(formData.price) || 0;
    const discountNum = parseFloat(formData.discountValue) || 0;
    if (!discountNum) return priceNum;

    const discounted =
      formData.discountType === "percent"
        ? priceNum - (priceNum * discountNum) / 100
        : priceNum - discountNum;

    return Number.isFinite(discounted) ? Math.max(discounted, 0) : priceNum;
  }, [formData.price, formData.discountValue, formData.discountType]);

  const handleImageUpload = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files);
    const previews = newFiles.map((file) => URL.createObjectURL(file));

    setImageFiles((prev) => [...prev, ...newFiles]);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...previews],
    }));

    // Set first uploaded image as default if no default is set
    if (defaultImageIndex === -1 && previews.length > 0) {
      setDefaultImageIndex(existingImageUrls.length);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleImageUpload(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveImage = (index: number) => {
    const totalImages = formData.images.length;
    const isExistingImage = index < existingImageUrls.length;

    if (isExistingImage) {
      // Remove from existing images
      setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      }));

      // Adjust default image index
      if (defaultImageIndex === index) {
        setDefaultImageIndex(-1);
      } else if (defaultImageIndex > index) {
        setDefaultImageIndex(defaultImageIndex - 1);
      }
    } else {
      // Remove from new files
      const fileIndex = index - existingImageUrls.length;
      setImageFiles((prev) => prev.filter((_, i) => i !== fileIndex));
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      }));

      // Adjust default image index
      if (defaultImageIndex === index) {
        setDefaultImageIndex(-1);
      } else if (defaultImageIndex > index) {
        setDefaultImageIndex(defaultImageIndex - 1);
      }
    }
  };

  const handleSetDefaultImage = (index: number) => {
    setDefaultImageIndex(index);
  };

  // Helper function to get full image URL
  const getImageUrl = (img: any): string => {
    // Ensure img is a string
    if (!img) return "";
    const imgStr = typeof img === "string" ? img : String(img);
    if (!imgStr || imgStr.trim() === "") return "";

    // If already a full URL, return as is
    if (imgStr.startsWith("http://") || imgStr.startsWith("https://") || imgStr.startsWith("data:")) {
      return imgStr;
    }
    // If it's a blob URL (from file preview), return as is
    if (imgStr.startsWith("blob:")) {
      return imgStr;
    }
    // If it starts with storage/ or public/, it's a storage path
    if (imgStr.startsWith("storage/") || imgStr.startsWith("public/")) {
      const baseUrl = "https://salonify.co.uk";
      const cleanPath = imgStr.startsWith("/") ? imgStr.slice(1) : imgStr;
      return `${baseUrl}/${cleanPath}`;
    }
    // If it's a relative path starting with /, prepend base URL
    if (imgStr.startsWith("/")) {
      return `https://salonify.co.uk${imgStr}`;
    }
    // Otherwise, assume it's a storage path and prepend base URL
    return `https://salonify.co.uk/storage/${imgStr}`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (taxDropdownRef.current && !taxDropdownRef.current.contains(event.target as Node)) {
        setIsTaxDropdownOpen(false);
      }
    };

    if (isTaxDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTaxDropdownOpen]);

  const handleTaxToggle = (taxId: number) => {
    setFormData((prev) => {
      const isSelected = prev.selectedTaxIds.includes(taxId);
      return {
        ...prev,
        selectedTaxIds: isSelected
          ? prev.selectedTaxIds.filter((id) => id !== taxId)
          : [...prev.selectedTaxIds, taxId],
      };
    });
  };

  const handleRemoveTax = (taxId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedTaxIds: prev.selectedTaxIds.filter((id) => id !== taxId),
    }));
  };

  // Filter taxes based on search query
  const filteredTaxes = useMemo(() => {
    if (!taxSearchQuery.trim()) return taxes;
    return taxes.filter(
      (tax) =>
        tax.name.toLowerCase().includes(taxSearchQuery.toLowerCase()) ||
        tax.percent.toString().includes(taxSearchQuery)
    );
  }, [taxes, taxSearchQuery]);

  // Get selected tax objects
  const selectedTaxes = useMemo(() => {
    return taxes.filter((tax) => formData.selectedTaxIds.includes(tax.id));
  }, [taxes, formData.selectedTaxIds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const priceNum = parseFloat(formData.price) || 0;
    const discountNum = parseFloat(formData.discountValue) || 0;

    // Discount validation
    if (discountNum > 0) {
      if (formData.discountType === "percent" && discountNum > 100) {
        toast.error("Discount percentage cannot exceed 100%");
        return;
      }
      if (formData.discountType === "amount" && discountNum > priceNum) {
        toast.error("Discount amount cannot exceed the product price");
        return;
      }
    }

    // Use selected tax IDs array directly
    const taxIds = formData.selectedTaxIds.length > 0 ? formData.selectedTaxIds : undefined;

    // Determine discount (only send if not 0)
    const discount = discountNum > 0 ? discountNum : undefined;

    let status: Product["status"] = product?.status || "In Stock";
    if (typeof product?.stock === "number") {
      if (product.stock <= 0) status = "Out of Stock";
      else if (product.stock < 10) status = "Low Stock";
    }

    await onSave({
      id: product?.id,
      name: formData.name,
      sku: formData.sku || undefined,
      barcode: formData.barcode || undefined,
      price: priceNum,
      discount,
      discountType: formData.discountType,
      discountedPrice,
      description: formData.description || undefined,
      taxIds,
      images: formData.images,
      image: formData.images[0],
      stock: product?.stock,
      status,
      // Include image files and existing URLs for API handling
      imageFiles,
      existingImageUrls,
      defaultImage: defaultImageIndex >= 0 ? formData.images[defaultImageIndex] : undefined,
    } as any);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-0 sm:p-4 overflow-y-auto z-[1000]">
      <div className="bg-white sm:rounded-xl shadow-xl w-full max-w-4xl my-0 sm:my-8 min-h-screen sm:min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {product ? "Edit product" : "Add new product"}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            <section className="border border-gray-200 rounded-lg">
              <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Product
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      SKU
                    </label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) =>
                        setFormData({ ...formData, sku: e.target.value })
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Barcode
                    </label>
                    <input
                      type="text"
                      value={formData.barcode}
                      onChange={(e) =>
                        setFormData({ ...formData, barcode: e.target.value })
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Select Tax
                    </label>
                    <div className="relative" ref={taxDropdownRef}>
                      {/* Selected Tax Chips */}
                      <div
                        className="w-full rounded-lg border border-gray-300 bg-purple-50 px-3 py-2 min-h-[42px] flex flex-wrap gap-2 items-center cursor-pointer"
                        onClick={() => setIsTaxDropdownOpen(!isTaxDropdownOpen)}
                      >
                        {selectedTaxes.length > 0 ? (
                          selectedTaxes.map((tax) => (
                            <span
                              key={tax.id}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-[#885ABB] text-white text-sm rounded-md"
                            >
                              {tax.name} {tax.percent}%
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveTax(tax.id);
                                }}
                                className="hover:bg-[#6f47a2] rounded-full p-0.5 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">Select taxes...</span>
                        )}
                        <ChevronDown
                          className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${isTaxDropdownOpen ? "rotate-180" : ""
                            }`}
                        />
                      </div>

                      {/* Dropdown Menu */}
                      {isTaxDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {/* Search Input */}
                          <div className="p-2 border-b border-gray-200">
                            <input
                              type="text"
                              placeholder="Search taxes..."
                              value={taxSearchQuery}
                              onChange={(e) => setTaxSearchQuery(e.target.value)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Tax Options */}
                          <div className="p-2">
                            {loadingTaxes ? (
                              <div className="text-sm text-gray-500 text-center py-4">
                                Loading taxes...
                              </div>
                            ) : filteredTaxes.length === 0 ? (
                              <div className="text-sm text-gray-500 text-center py-4">
                                No taxes found
                              </div>
                            ) : (
                              filteredTaxes.map((tax) => {
                                const isSelected = formData.selectedTaxIds.includes(tax.id);
                                return (
                                  <label
                                    key={tax.id}
                                    className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleTaxToggle(tax.id)}
                                      className="h-4 w-4 text-[#885ABB] border-gray-300 rounded focus:ring-[#885ABB]"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900">
                                        {tax.name}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {tax.percent}%
                                      </div>
                                    </div>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Price ({currencySymbol}) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price === "0" ? "" : formData.price}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, price: val });
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Discount
                    </label>
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                      <input
                        type="number"
                        step="0.01"
                        value={formData.discountValue === "0" ? "" : formData.discountValue}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discountValue: e.target.value,
                          })
                        }
                        className="w-full border-r border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                      />
                      <select
                        value={formData.discountType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discountType: e.target.value as "percent" | "amount",
                          })
                        }
                        className="bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none"
                      >
                        <option value="percent">Percent</option>
                        <option value="amount">Fixed</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Price after discount
                    </label>
                    <div className="h-11 flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 text-gray-900 font-semibold">
                      {formData.price && formData.discountValue && parseFloat(formData.discountValue) > 0
                        ? `${currencySymbol}${discountedPrice.toFixed(2)}`
                        : "--"}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Product Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={6}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                  />
                </div>
              </div>
            </section>

            <section className="border border-gray-200 rounded-lg">
              <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                  Product Images
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-300 bg-gray-50"
                    }`}
                >
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                    <ImagePlus className="h-6 w-6 text-purple-600" />
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Drop files here to upload or
                    <label className="ml-1 cursor-pointer text-purple-600 font-semibold">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleImageUpload(e.target.files)}
                      />
                      browse
                    </label>
                  </p>
                  <p className="text-xs text-gray-500">
                    You can add multiple images
                  </p>
                </div>

                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {formData.images.map((img, index) => {
                      const isDefault = defaultImageIndex === index;
                      const isBroken = brokenImageIndices.has(index);
                      return (
                        <div
                          key={`img-${index}-${typeof img === 'string' ? img.substring(0, 20) : index}`}
                          className="relative rounded-lg overflow-hidden border-2 border-gray-200"
                        >
                          {isBroken ? (
                            <div className="h-32 w-full bg-gray-100 flex items-center justify-center">
                              <div className="text-center">
                                <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                </svg>
                                <p className="text-xs text-gray-500">product-{index}</p>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={getImageUrl(img)}
                              alt={`product-${index}`}
                              className={`h-32 w-full object-cover ${isDefault ? "ring-2 ring-purple-600" : ""
                                }`}
                              onError={() => {
                                // Mark this image as broken using React state
                                setBrokenImageIndices((prev) => new Set(prev).add(index));
                              }}
                            />
                          )}
                          {isDefault && (
                            <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded">
                              Default
                            </div>
                          )}
                          <div className="absolute top-2 right-2 flex gap-1">
                            {!isDefault && (
                              <button
                                type="button"
                                onClick={() => handleSetDefaultImage(index)}
                                className="rounded-full bg-white/80 p-1 text-purple-600 hover:bg-white"
                                title="Set as default"
                              >
                                <ImagePlus className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="rounded-full bg-white/80 p-1 text-red-500 hover:bg-white"
                              title="Remove image"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 bg-gray-100 text-gray-900 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
